import { useState, useEffect, useCallback, useRef } from "react";
import { Bot, Play, Square } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * 🎴 AUTO 24/7 mode for HiLo Cards — the server bot plays strong-edge cards
 * only (≤2 HIGHER / ≥7 LOWER), with ⚔️ 120% combat that WAITS to see winning
 * numbers print before betting back. SL & TP are mandatory rails.
 */
const HiLoCardsAuto = ({ authorized, accountId, accessToken, onConnect }) => {
  const [st, setSt] = useState(null);
  const [stake, setStake] = useState("0.35");
  const [sl, setSl] = useState("10");
  const [tp, setTp] = useState("5");
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  const poll = useCallback(async () => {
    if (!accountId) return;
    try {
      const res = await api.get(`/hilo-cards-bot/status/${accountId}`);
      setSt(res.data);
    } catch { /* ignore */ }
  }, [accountId]);

  useEffect(() => {
    poll();
    timer.current = setInterval(poll, 4000);
    return () => clearInterval(timer.current);
  }, [poll]);

  const start = async () => {
    if (!authorized) { onConnect?.(); return; }
    setBusy(true);
    try {
      const res = await api.post("/hilo-cards-bot/start", {
        account_id: accountId, access_token: accessToken || "",
        stake: Number(stake) || 0.35, sl: Number(sl), tp: Number(tp),
        auto_restart: false, disclaimer_accepted: true,
      });
      setSt(res.data);
      toast.success("🎴 Auto Cards ON — strong-edge cards only, ⚔️ 120% combat armed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Auto Cards");
    }
    setBusy(false);
  };

  const stop = async () => {
    setBusy(true);
    try {
      await api.post(`/hilo-cards-bot/stop/${accountId}`);
      toast.success("⏹ Auto Cards off");
      poll();
    } catch { toast.error("Could not stop the bot"); }
    setBusy(false);
  };

  const running = !!st?.running;
  const net = Number(st?.net || 0);

  return (
    <div data-testid="hilocards-auto" className="rounded-lg border border-vts-line bg-vts-surface/40 p-3 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Bot className="w-4 h-4 text-amber-300" />
        <p className="text-xs font-bold text-white">AUTO 24/7</p>
        <span className="text-[9px] font-mono text-vts-silver/50">60/40 mirror cards (3⬆ / 6⬇, same risk both sides) · ⚔️ 120% combat · waits for winning numbers · STOPS at SL/TP</span>
        {running && (
          <span data-testid="hilocards-auto-live" className="ml-auto px-1.5 py-0.5 rounded bg-vts-profit/15 border border-vts-profit/40 text-[9px] font-bold font-mono text-vts-profit animate-pulse">● ON</span>
        )}
      </div>

      {running ? (
        <>
          <div data-testid="hilocards-auto-stats" className="grid grid-cols-4 gap-2 text-center">
            {[
              { k: "net", label: "Net", val: `${net >= 0 ? "+" : "−"}$${Math.abs(net).toFixed(2)}`, c: net >= 0 ? "text-vts-profit" : "text-vts-loss" },
              { k: "wl", label: "W / L", val: `${st?.wins ?? 0}/${st?.losses ?? 0}`, c: "text-white" },
              { k: "banked", label: "Banked", val: `$${Number(st?.banked || 0).toFixed(2)}`, c: "text-amber-300" },
              { k: "phase", label: "Phase", val: st?.phase === "watching" ? `👀 ${st?.watch_proofs ?? 0}/2` : (st?.phase || "—"), c: "text-vts-silver/80" },
            ].map((x) => (
              <div key={x.k} data-testid={`hilocards-auto-${x.k}`} className="rounded bg-vts-panel border border-vts-line p-1.5">
                <p className={`text-[11px] font-bold font-mono ${x.c}`}>{x.val}</p>
                <p className="text-[8px] font-mono text-vts-silver/50">{x.label}</p>
              </div>
            ))}
          </div>
          {(st?.feed || []).length > 0 && (
            <div data-testid="hilocards-auto-feed" className="space-y-0.5 max-h-24 overflow-y-auto">
              {[...st.feed].reverse().slice(0, 6).map((f, i) => (
                <p key={i} className="text-[9px] font-mono text-vts-silver/55">
                  <span className="text-vts-silver/35">{String(f.ts).slice(11, 19)}</span> {f.text}
                </p>
              ))}
            </div>
          )}
          <button data-testid="hilocards-auto-stop-btn" onClick={stop} disabled={busy}
            className="w-full py-2 rounded-md bg-vts-loss/80 hover:bg-vts-loss text-white text-xs font-bold flex items-center justify-center gap-1.5 disabled:opacity-50">
            <Square className="w-3.5 h-3.5" /> STOP AUTO CARDS
          </button>
        </>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2">
            {[
              { id: "hilocards-auto-stake", label: "Stake $", val: stake, set: setStake },
              { id: "hilocards-auto-sl", label: "🛑 Stop-loss $", val: sl, set: setSl },
              { id: "hilocards-auto-tp", label: "🎯 Take-profit $", val: tp, set: setTp },
            ].map((f) => (
              <label key={f.id} className="block">
                <span className="text-[8px] font-mono text-vts-silver/50">{f.label}</span>
                <input data-testid={f.id} type="number" min="0.35" step="0.01" value={f.val}
                  onChange={(e) => f.set(e.target.value)}
                  className="w-full mt-0.5 px-2 py-1.5 rounded bg-vts-panel border border-vts-line text-xs font-mono text-white" />
              </label>
            ))}
          </div>
          <button data-testid="hilocards-auto-start-btn" onClick={start} disabled={busy}
            className="w-full py-2 rounded-md bg-amber-500/80 hover:bg-amber-500 text-black text-xs font-bold flex items-center justify-center gap-1.5 disabled:opacity-50">
            <Play className="w-3.5 h-3.5" /> START AUTO CARDS (24/7)
          </button>
          {st?.stopped_reason && (
            <p data-testid="hilocards-auto-stopped" className="text-[9px] font-mono text-amber-300/80 text-center">
              Last run ended: {String(st.stopped_reason).replace(/_/g, " ")}{st?.banked ? ` · banked $${Number(st.banked).toFixed(2)}` : ""}
            </p>
          )}
        </>
      )}

      {/* 📜 bot trade history — always visible */}
      {(st?.recent || []).length > 0 && (
        <div data-testid="hilocards-auto-history" className="space-y-1 max-h-40 overflow-y-auto">
          <p className="text-[9px] font-mono text-vts-silver/50 uppercase">Bot history</p>
          {st.recent.map((r, i) => (
            <div key={i} className="flex items-center gap-2 text-[10px] font-mono rounded border border-vts-line/40 bg-vts-panel px-2 py-1">
              <span className="text-vts-silver/45">{String(r.settled_at || "").slice(11, 19)}</span>
              <span className="text-white">🎴{r.barrier} {r.contract_type === "DIGITOVER" ? "⬆" : "⬇"} → {r.digit ?? "?"}</span>
              <span className="text-vts-silver/40">${Number(r.stake).toFixed(2)}</span>
              <span className={`ml-auto font-bold ${Number(r.profit) > 0 ? "text-vts-profit" : "text-vts-loss"}`}>
                {Number(r.profit) > 0 ? "+" : "−"}${Math.abs(Number(r.profit)).toFixed(2)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HiLoCardsAuto;
